import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Globe, ChevronDown } from "lucide-react";
import { languages } from "@/i18n";
import { cn } from "@/lib/utils";

interface LanguageSwitcherProps {
  /** "compact" shows only flag+code, opens dropdown downward (for navbar).
   *  "full" shows flag+label, opens upward (for footer). */
  variant?: "compact" | "full";
}

export function LanguageSwitcher({ variant = "full" }: LanguageSwitcherProps) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const ref = useRef<HTMLDivElement>(null);
  const current = languages.find((l) => l.code === i18n.language) || languages[0];

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const changeLanguage = (code: string) => {
    const pathParts = location.pathname.split("/");
    if (pathParts.length >= 2 && languages.some((l) => l.code === pathParts[1])) {
      pathParts[1] = code;
    } else {
      pathParts.splice(1, 0, code);
    }
    const newPath = pathParts.join("/") || "/";
    i18n.changeLanguage(code);
    localStorage.setItem("seaventis-lang", code);
    setOpen(false);
    navigate(newPath, { replace: true });
  };

  const isCompact = variant === "compact";

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={`Change language — currently ${current.label}`}
        className={cn(
          "flex items-center gap-1.5 rounded-md border border-white/10 bg-white/5 hover:bg-white/10 transition-colors text-white/70 hover:text-white",
          isCompact ? "px-2 py-1.5 text-xs" : "px-3 py-2 text-sm gap-2"
        )}
      >
        <Globe className={cn(isCompact ? "h-3.5 w-3.5" : "h-4 w-4")} />
        <span>{current.flag} {isCompact ? current.code.toUpperCase() : current.label}</span>
        <ChevronDown className={cn("transition-transform", isCompact ? "h-3 w-3" : "h-3.5 w-3.5", open && "rotate-180")} />
      </button>
      {open && (
        <div
          role="listbox"
          aria-label="Select language"
          className={cn(
            "absolute z-50 rounded-xl border border-white/10 bg-[hsl(220,28%,8%)] backdrop-blur-xl shadow-2xl p-2 min-w-[170px]",
            isCompact
              ? "top-full right-0 mt-2"
              : "bottom-full left-0 mb-2"
          )}
        >
          <div className="px-3 py-1.5 text-xs text-white/50 uppercase tracking-wider font-semibold">
            {t("footer.language")}
          </div>
          {languages.map((lang) => (
            <button
              key={lang.code}
              role="option"
              aria-selected={lang.code === i18n.language}
              onClick={() => changeLanguage(lang.code)}
              className={cn(
                "flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors w-full text-left",
                lang.code === i18n.language
                  ? "text-teal-glow bg-teal-glow/10"
                  : "text-white/60 hover:text-white hover:bg-white/5"
              )}
            >
              <span className="text-base">{lang.flag}</span>
              <span>{lang.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
